import React from 'react';
import {
  createFirestoreBackup,
  downloadBackup,
  printBackupSummary,
  restoreBackupWithClear,
  upsertBackup,
  createSelectiveBackupForMaps,
} from './firestoreBackup';
import { BackupData } from './BackupData.interface';
import { Alert, AlertTitle, AlertDescription, AlertActions } from '../components_catalyst/alert';
import { Button } from '../components_catalyst/button';
import { useIsAdmin } from '../utils/useIsAdmin';

export function BackupPage() {
  const [isBackingUp, setIsBackingUp] = React.useState(false);
  const [lastBackupTime, setLastBackupTime] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [isRestoring, setIsRestoring] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const upsertFileInputRef = React.useRef<HTMLInputElement>(null);
  const [isRestoreAlertOpen, setIsRestoreAlertOpen] = React.useState(false);
  const [isUpsertAlertOpen, setIsUpsertAlertOpen] = React.useState(false);
  const [pendingFile, setPendingFile] = React.useState<File | null>(null);
  const [selectedMapUuids, setSelectedMapUuids] = React.useState('');

  const buttonBaseClasses = 'w-full sm:w-64 px-4 py-2 text-white rounded disabled:opacity-50';

  const handleBackup = async () => {
    setIsBackingUp(true);
    setError(null);
    try {
      // First create the backup
      const backup = await createFirestoreBackup();

      // Then download it
      downloadBackup(backup);

      // Update UI
      setLastBackupTime(new Date().toLocaleString());
      console.log('Backup completed:');
      printBackupSummary(backup);
    } catch (error) {
      console.error('Backup failed:', error);
      setError(error instanceof Error ? error.message : 'Backup failed');
    } finally {
      setIsBackingUp(false);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>, isUpsert: boolean) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setPendingFile(file);
    if (isUpsert) {
      setIsUpsertAlertOpen(true);
    } else {
      setIsRestoreAlertOpen(true);
    }

    // Reset file input
    event.target.value = '';
  };

  const handleRestore = async (file: File, shouldClear: boolean) => {
    setIsRestoring(true);
    setError(null);

    try {
      const fileContent = await file.text();
      const backup: BackupData = JSON.parse(fileContent);

      if (shouldClear) {
        await restoreBackupWithClear(backup);
      } else {
        await upsertBackup(backup);
      }

      setLastBackupTime(new Date().toLocaleString());
      console.log(`Backup ${shouldClear ? 'restored' : 'upserted'}:`);
      printBackupSummary(backup);
    } catch (error) {
      console.error('Restore failed:', error);
      setError(error instanceof Error ? error.message : 'Restore failed');
    } finally {
      setIsRestoring(false);
      setPendingFile(null);
    }
  };

  // Add handler for selective backup
  const handleSelectiveBackup = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsBackingUp(true);
    setError(null);
    try {
      // Parse UUIDs from input
      const uuids = selectedMapUuids
        .split(',')
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
      if (uuids.length === 0) {
        setError('Please enter at least one milestone map UUID.');
        setIsBackingUp(false);
        return;
      }
      const backup = await createSelectiveBackupForMaps(uuids);
      downloadBackup(backup);
      setLastBackupTime(new Date().toLocaleString());
      printBackupSummary(backup);
    } catch (error) {
      console.error('Selective backup failed:', error);
      setError(error instanceof Error ? error.message : 'Selective backup failed');
    } finally {
      setIsBackingUp(false);
    }
  };

  // TODO: Add a double check. When somebody is uploading/restoring
  // data from one environment to another, we should have a double check.
  // The alert should say something like:
  // "Are you sure you want to restore data from [source environment] to [target environment]?"
  // The alert should have a checkbox to confirm the action or type in the new
  // environment name.

  const isAdmin = useIsAdmin();
  console.log('BackupPage - isAdmin:', isAdmin);

  if (!isAdmin) {
    return <div>You are not authorized to access this page.</div>;
  }

  return (
    <div className="p-4">
      <h1 className="text-2xl font-bold mb-4">Firestore Backup Utility</h1>

      <div className="space-y-4">
        <button
          onClick={handleBackup}
          disabled={isBackingUp || isRestoring}
          className={`${buttonBaseClasses} bg-blue-500 hover:bg-blue-600`}
        >
          {isBackingUp ? 'Creating Backup...' : 'Download Backup'}
        </button>

        <div className="flex flex-col gap-2">
          <input
            type="file"
            ref={fileInputRef}
            accept=".json"
            className="hidden"
            onChange={(e) => handleFileSelect(e, false)}
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={isBackingUp || isRestoring}
            className={`${buttonBaseClasses} bg-red-500 hover:bg-red-600`}
          >
            {isRestoring ? 'Restoring...' : 'Restore (Clear & Replace)'}
          </button>

          <input
            type="file"
            ref={upsertFileInputRef}
            accept=".json"
            className="hidden"
            onChange={(e) => handleFileSelect(e, true)}
          />
          <button
            onClick={() => upsertFileInputRef.current?.click()}
            disabled={isBackingUp || isRestoring}
            className={`${buttonBaseClasses} bg-yellow-500 hover:bg-yellow-600`}
          >
            {isRestoring ? 'Upserting...' : 'Restore (Upsert Only)'}
          </button>

          {/* Clear & Replace Alert */}
          <Alert open={isRestoreAlertOpen} onClose={() => setIsRestoreAlertOpen(false)}>
            <AlertTitle>Confirm Clear & Replace</AlertTitle>
            <AlertDescription>
              This will delete all existing data and replace it with the backup data. This action
              cannot be undone. Recommendation: Backup your data before running this operation.
            </AlertDescription>
            <AlertActions>
              <Button plain onClick={() => setIsRestoreAlertOpen(false)}>
                Cancel
              </Button>
              <Button
                color="red"
                onClick={() => {
                  setIsRestoreAlertOpen(false);
                  if (pendingFile) {
                    handleRestore(pendingFile, true);
                  }
                }}
              >
                Clear & Replace
              </Button>
            </AlertActions>
          </Alert>

          {/* Upsert Alert */}
          <Alert open={isUpsertAlertOpen} onClose={() => setIsUpsertAlertOpen(false)}>
            <AlertTitle>Confirm Upsert</AlertTitle>
            <AlertDescription>
              This will add or update records from the backup file. It won't delete existing data.
              Recommendation: Backup your data before running this operation.
            </AlertDescription>
            <AlertActions>
              <Button plain onClick={() => setIsUpsertAlertOpen(false)}>
                Cancel
              </Button>
              <Button
                color="yellow"
                onClick={() => {
                  setIsUpsertAlertOpen(false);
                  if (pendingFile) {
                    handleRestore(pendingFile, false);
                  }
                }}
              >
                Upsert
              </Button>
            </AlertActions>
          </Alert>

          {error && <div className="text-red-500 mt-4">{error}</div>}
          {lastBackupTime && (
            <div className="text-gray-500 mt-4">Last operation: {lastBackupTime}</div>
          )}
        </div>

        <div className="mt-8 text-sm text-gray-600">
          <h2 className="font-semibold mb-2">About this utility:</h2>
          <ul className="list-disc list-inside space-y-1">
            <li>Creates a backup of your Firestore collections</li>
            <li>Downloads as a JSON file</li>
            <li>Includes timestamp and all document data</li>
            <li>Filename format: firestore-backup-YYYY-MM-DD.utc.environment.json</li>
            <li>All timestamps are in UTC timezone</li>
            <li>Supports full database restore (clear & replace)</li>
            <li>Supports selective update (upsert only)</li>
          </ul>
        </div>

        {/* Milestone Map UUID(s) Form */}
        <form className="mb-6" onSubmit={handleSelectiveBackup}>
          <label className="block font-semibold mb-1" htmlFor="map-uuids">
            Milestone Map UUID(s)
          </label>
          <div className="text-sm text-gray-600 mb-2">
            Enter one or more comma separated milestone map UUIDs. Click download to download only
            your specified maps and their corresponding objectives.
          </div>
          <input
            id="map-uuids"
            type="text"
            className="w-full border rounded px-3 py-2 mb-2"
            placeholder="e.g. uuid1, uuid2, ..."
            value={selectedMapUuids}
            onChange={(e) => setSelectedMapUuids(e.target.value)}
          />
          <button
            type="submit"
            disabled={isBackingUp || isRestoring}
            className={`${buttonBaseClasses} bg-green-600 hover:bg-green-700`}
          >
            {isBackingUp ? 'Downloading...' : 'Download Selected Maps'}
          </button>
        </form>
      </div>
    </div>
  );
}
